var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
//@ts-ignore
import { useBranch } from 'baobab-react/hooks';
import { ColumnMetaDataContext } from '@common/api';
import { useLanguage } from '@base/LanguageProvider';
import { parseDateWithImportFormat } from '@main/import/util';
import { useImportJobRows } from '@data/importJob/importJobActions';
import * as baobab from '@data/baobab/baobab';
function generateColumnFilters(columnFilterGenerateInfo) {
    return columnFilterGenerateInfo.map(function (info) {
        var e_1, _a;
        // sort values
        var stringValues = Array.from(info.stringValueSet);
        stringValues.sort();
        var numberValues = Array.from(info.numberValueSet);
        numberValues.sort(function (a, b) { return a - b; });
        var allValues = __spreadArray(__spreadArray([], __read(stringValues), false), __read(numberValues), false);
        // map filter
        var showValues = {};
        try {
            for (var allValues_1 = __values(allValues), allValues_1_1 = allValues_1.next(); !allValues_1_1.done; allValues_1_1 = allValues_1.next()) {
                var valueKey = allValues_1_1.value;
                showValues[valueKey] = true;
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (allValues_1_1 && !allValues_1_1.done && (_a = allValues_1.return)) _a.call(allValues_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return {
            hasEmptyCells: info.hasEmptyCells,
            showEmptyCells: info.hasEmptyCells,
            sortedValues: allValues,
            showValues: showValues,
        };
    });
}
function columnFiltersHaveSameValues(columnFilter1, columnFilter2) {
    if (columnFilter1.hasEmptyCells !== columnFilter2.hasEmptyCells) {
        // one has empty cells and one doesn't
        return false;
    }
    if (columnFilter1.sortedValues.length !== columnFilter2.sortedValues.length) {
        // different lengths of values (should hit most cases)
        return false;
    }
    for (var i = 0; i < columnFilter1.sortedValues.length; i++) {
        // check if every value is the same (should run rarely)
        if (columnFilter1.sortedValues[i] !== columnFilter2.sortedValues[i]) {
            // some value is not in same position (arrays are sorted, so values are not the same)
            return false;
        }
    }
    return true;
}
export function useImportJobTableData(importJob) {
    var importJobRows = useImportJobRows(importJob.id, { notifyOnChangeProps: ['data'] }).data;
    var columnFilters = useBranch({
        columnFilters: ['state', 'importColumnFilters', importJob.id],
    }).columnFilters;
    var _a = useLanguage(), formatLocaleDate = _a.formatLocaleDate, excelLocalizedNumberFormat = _a.excelLocalizedNumberFormat;
    if (importJobRows === undefined || importJobRows.length === 0) {
        return [];
    }
    var columnMetas = importJob.metadata.columnMetas;
    var columnFilterGenerateInfo = new Array(columnMetas.length);
    for (var columnIndex = 0; columnIndex < columnMetas.length; columnIndex++) {
        columnFilterGenerateInfo[columnIndex] = {
            hasEmptyCells: false,
            stringValueSet: new Set(),
            numberValueSet: new Set(),
        };
    }
    var rows = [];
    for (var rowIndex = 0; rowIndex < importJobRows.length; rowIndex++) {
        var rowData = importJobRows[rowIndex].data;
        var formattedRowData = [];
        var rowStyle = {};
        var skipThisRow = false;
        for (var columnIndex = 0; columnIndex < rowData.length && columnMetas.length; columnIndex++) {
            var columnMeta = columnMetas[columnIndex];
            if (columnMeta === undefined) {
                continue;
            }
            var filterGenInfo = columnFilterGenerateInfo[columnIndex];
            var columnFilter = columnFilters === null || columnFilters === void 0 ? void 0 : columnFilters[columnIndex];
            var rawValue = rowData[columnIndex];
            if (rawValue === null) {
                // empty cell
                formattedRowData.push(null);
                filterGenInfo.hasEmptyCells = true;
                // check if filter exists and if empty cells should be shown
                if (columnFilter !== undefined && !columnFilter.showEmptyCells) {
                    skipThisRow = true;
                }
                continue;
            }
            if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
                // unknown type of value
                formattedRowData.push(null);
                filterGenInfo.hasEmptyCells = true;
                // check if filter exists and if empty cells should be shown
                if (columnFilter !== undefined && !columnFilter.showEmptyCells) {
                    skipThisRow = true;
                }
                continue;
            }
            if (columnMeta.dataContext === ColumnMetaDataContext.IDENTIFIER) {
                // check if dimension tag with same name has been detected
                var stringValue = typeof rawValue === 'string' ? rawValue : rawValue.toString();
                var detectedTagMap = columnMeta.detectedTagMap;
                var detectedTagIds = detectedTagMap === null || detectedTagMap === void 0 ? void 0 : detectedTagMap[stringValue];
                if (detectedTagIds !== undefined && detectedTagIds.length > 0) {
                    rowStyle[columnIndex] = 'tag';
                }
                formattedRowData.push(stringValue);
                filterGenInfo.stringValueSet.add(stringValue);
                // check if filter exists and if value should be shown
                if (columnFilter !== undefined && !columnFilter.showValues[stringValue]) {
                    skipThisRow = true;
                }
            }
            else if (columnMeta.dataContext === ColumnMetaDataContext.DATETIME) {
                // parse and format date
                var parsedDate = parseDateWithImportFormat(rawValue, columnMeta.dateFormat);
                if (parsedDate === null) {
                    // date can't be parsed
                    formattedRowData.push(null);
                    filterGenInfo.hasEmptyCells = true;
                    // check if filter exists and if empty cells should be shown
                    if (columnFilter !== undefined && !columnFilter.showEmptyCells) {
                        skipThisRow = true;
                    }
                }
                else {
                    var formattedDate = formatLocaleDate(parsedDate);
                    formattedRowData.push(formattedDate);
                    filterGenInfo.numberValueSet.add(parsedDate.getTime());
                    // check if filter exists and if value should be shown
                    if (columnFilter !== undefined && !columnFilter.showValues[parsedDate.getTime()]) {
                        skipThisRow = true;
                    }
                }
            }
            else if (columnMeta.dataContext === ColumnMetaDataContext.VALUES ||
                columnMeta.dataContext === ColumnMetaDataContext.TIMEPOINT) {
                // format numbers by user locale
                var numberValue = typeof rawValue === 'string' ? Number.parseFloat(rawValue) : rawValue;
                if (Number.isNaN(numberValue)) {
                    // number
                    formattedRowData.push(null);
                    filterGenInfo.hasEmptyCells = true;
                    // check if filter exists and if empty cells should be shown
                    if (columnFilter !== undefined && !columnFilter.showEmptyCells) {
                        skipThisRow = true;
                    }
                }
                else {
                    var formattedValue = excelLocalizedNumberFormat(numberValue);
                    formattedRowData.push(formattedValue);
                    rowStyle[columnIndex] = 'number';
                    filterGenInfo.numberValueSet.add(numberValue);
                    // check if filter exists and if value should be shown
                    if (columnFilter !== undefined && !columnFilter.showValues[numberValue]) {
                        skipThisRow = true;
                    }
                }
            }
            else {
                // data context is one of [NONE, DESCRIPTION, CUSTOM, TAG_DESCRIPTION]
                // show value as-is
                formattedRowData.push(rawValue);
                if (typeof rawValue === 'string') {
                    filterGenInfo.stringValueSet.add(rawValue);
                }
                else if (typeof rawValue === 'number') {
                    filterGenInfo.numberValueSet.add(rawValue);
                }
                // check if filter exists and if value should be shown
                if (columnFilter !== undefined && !columnFilter.showValues[rawValue]) {
                    skipThisRow = true;
                }
            }
        }
        if (!skipThisRow) {
            rows.push({
                rowId: importJobRows[rowIndex].id,
                formattedRowData: formattedRowData,
                originalRowData: __spreadArray([], __read(rowData), false),
                style: rowStyle,
            });
        }
    }
    // generate new column filters
    var generatedColumnFilters = generateColumnFilters(columnFilterGenerateInfo);
    // check if generated filters have different values to previous filters
    var shouldSaveNewFilters = false;
    for (var columnIndex = 0; columnIndex < generatedColumnFilters.length; columnIndex++) {
        var generatedColumnFilter = generatedColumnFilters[columnIndex];
        var previousColumnFilter = columnFilters === null || columnFilters === void 0 ? void 0 : columnFilters[columnIndex];
        if (previousColumnFilter === undefined) {
            // there was no filter previously - first generation
            shouldSaveNewFilters = true;
            break;
        }
        if (!columnFiltersHaveSameValues(generatedColumnFilter, previousColumnFilter)) {
            // some filters differ in values
            shouldSaveNewFilters = true;
            break;
        }
    }
    if (shouldSaveNewFilters) {
        // save new filters to baobab
        baobab.root.set(['state', 'importColumnFilters', importJob.id], generatedColumnFilters);
    }
    return rows;
}
